import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle2, 
  Clock,
  FileText,
  Globe,
  Lock,
  Eye,
  AlertCircle,
  TrendingUp,
  BarChart3,
} from 'lucide-react';
import { Card } from '../ui/Card'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { cn } from '../../utils/cn'
import { NOT_MEASURED, NotMeasuredNotice } from './NotMeasured'
import type { SocketCloudMcpSecurityComponents } from '@aaimplatform/socket-cloud-types';

type ComplianceCheck = SocketCloudMcpSecurityComponents['schemas']['ComplianceRule'];
type DataFlow = SocketCloudMcpSecurityComponents['schemas']['DataFlowRecord'];

// No data source feeds this page yet (GH #3969). Both lists stay empty and every
// summary reads "Not measured" until a later GH #3958 unit wires one.
const NO_COMPLIANCE_CHECKS: ComplianceCheck[] = [];
const NO_DATA_FLOWS: DataFlow[] = [];

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'compliant': return <CheckCircle2 className="w-4 h-4 text-green-400" />;
    case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
    case 'violation': return <AlertCircle className="w-4 h-4 text-red-400" />;
    default: return <Clock className="w-4 h-4 text-gray-400" />;
  }
};

const getStatusBadgeVariant = (status: string) => {
  switch (status) {
    case 'compliant': return 'success';
    case 'warning': return 'warning';
    case 'violation': return 'danger';
    default: return 'secondary';
  }
};

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'data-residency': return <Globe className="w-4 h-4" />;
    case 'encryption': return <Lock className="w-4 h-4" />;
    case 'audit': return <FileText className="w-4 h-4" />;
    case 'access-control': return <Shield className="w-4 h-4" />;
    case 'monitoring': return <Eye className="w-4 h-4" />;
    default: return <AlertCircle className="w-4 h-4" />;
  }
};

const getRiskColor = (score: number) => {
  if (score <= 3) {
return 'text-green-400';
}
  if (score <= 6) {
return 'text-yellow-400';
}
  return 'text-red-400';
};

export const ComplianceMonitor: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  const complianceStats = useMemo(() => {
    const total = NO_COMPLIANCE_CHECKS.length;
    if (total === 0) {
      return null;
    }
    const compliant = NO_COMPLIANCE_CHECKS.filter(c => c.status === 'compliant').length;
    const warnings = NO_COMPLIANCE_CHECKS.filter(c => c.status === 'warning').length;
    const violations = NO_COMPLIANCE_CHECKS.filter(c => c.status === 'violation').length;
    
    return {
      total,
      compliant,
      warnings,
      violations,
      score: Math.round((compliant / total) * 100),
    };
  }, []);

  const dataFlowStats = useMemo(() => {
    if (NO_DATA_FLOWS.length === 0) {
      return null;
    }
    const totalVolume = NO_DATA_FLOWS.reduce((sum, flow) => sum + flow.volume, 0);
    const highRiskFlows = NO_DATA_FLOWS.filter(flow => flow.riskScore > 6).length;
    const crossBorderFlows = NO_DATA_FLOWS.filter(flow => flow.crossBorder).length;
    const encryptedFlows = NO_DATA_FLOWS.filter(flow => flow.encrypted).length;
    
    return {
      totalVolume,
      highRiskFlows,
      crossBorderFlows,
      encryptedFlows,
      encryptionRate: Math.round((encryptedFlows / NO_DATA_FLOWS.length) * 100),
    };
  }, []);

  const filteredChecks = selectedCategory 
    ? NO_COMPLIANCE_CHECKS.filter(check => check.category === selectedCategory)
    : NO_COMPLIANCE_CHECKS;

  return (
    <div className="space-y-6">
      {/* Compliance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-white">{complianceStats ? `${complianceStats.score}%` : NOT_MEASURED}</div>
              <div className="text-sm text-gray-400">Compliance Score</div>
            </div>
            <Shield className="w-8 h-8 text-green-400" />
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-green-400">{complianceStats ? complianceStats.compliant : NOT_MEASURED}</div>
              <div className="text-sm text-gray-400">Compliant Checks</div>
            </div>
            <CheckCircle2 className="w-8 h-8 text-green-400" />
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-yellow-400">{complianceStats ? complianceStats.warnings : NOT_MEASURED}</div>
              <div className="text-sm text-gray-400">Warnings</div>
            </div>
            <AlertTriangle className="w-8 h-8 text-yellow-400" />
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-red-400">{complianceStats ? complianceStats.violations : NOT_MEASURED}</div>
              <div className="text-sm text-gray-400">Violations</div>
            </div>
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compliance Checks */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Compliance Checks</h3>
            <div className="flex gap-2">
              {['data-residency', 'encryption', 'audit', 'access-control', 'monitoring'].map(category => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                  className="text-xs"
                >
                  {getCategoryIcon(category)}
                </Button>
              ))}
            </div>
          </div>
          
          {filteredChecks.length === 0 && (
            <NotMeasuredNotice message="No compliance checks are recorded: no data source is connected." />
          )}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {filteredChecks.map(check => (
              <motion.div
                key={check.id}
                className={cn(
                  'p-3 rounded-lg border',
                  check.status === 'compliant' ? 'bg-green-900/20 border-green-700' :
                  check.status === 'warning' ? 'bg-yellow-900/20 border-yellow-700' :
                  check.status === 'violation' ? 'bg-red-900/20 border-red-700' :
                  'bg-gray-800 border-gray-700',
                )}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(check.category)}
                    <span className="font-medium text-white text-sm">{check.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getStatusBadgeVariant(check.status)} className="text-xs">
                      {check.status}
                    </Badge>
                    {getStatusIcon(check.status)}
                  </div>
                </div>
                
                <p className="text-xs text-gray-300 mb-2">{check.description}</p>
                
                {check.remediation && (
                  <div className="text-xs text-primary-400 bg-primary-900/20 p-2 rounded">
                    <strong>Remediation:</strong> {check.remediation}
                  </div>
                )}
                
                <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                  <span>Severity: {check.severity}</span>
                  <span>Last checked: {new Date(check.lastChecked).toLocaleTimeString()}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </Card>

        {/* Data Flow Monitoring */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Data Flow Analysis</h3>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-800 rounded p-3">
              <div className="text-lg font-bold text-primary-400">{dataFlowStats ? dataFlowStats.totalVolume.toLocaleString() : NOT_MEASURED}</div>
              <div className="text-xs text-gray-400">Requests/Hour</div>
            </div>
            <div className="bg-gray-800 rounded p-3">
              <div className="text-lg font-bold text-purple-400">{dataFlowStats ? `${dataFlowStats.encryptionRate}%` : NOT_MEASURED}</div>
              <div className="text-xs text-gray-400">Encrypted</div>
            </div>
            <div className="bg-gray-800 rounded p-3">
              <div className="text-lg font-bold text-yellow-400">{dataFlowStats ? dataFlowStats.crossBorderFlows : NOT_MEASURED}</div>
              <div className="text-xs text-gray-400">Cross-Border</div>
            </div>
            <div className="bg-gray-800 rounded p-3">
              <div className="text-lg font-bold text-red-400">{dataFlowStats ? dataFlowStats.highRiskFlows : NOT_MEASURED}</div>
              <div className="text-xs text-gray-400">High Risk</div>
            </div>
          </div>
          
          {NO_DATA_FLOWS.length === 0 && (
            <NotMeasuredNotice message="No data flows are recorded: no data source is connected." />
          )}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {NO_DATA_FLOWS.map(flow => (
              <div key={flow.id} className="p-2 bg-gray-800 rounded text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-white">{flow.source} → {flow.destination}</span>
                  <div className="flex items-center gap-2">
                    <span className={cn('font-bold', getRiskColor(flow.riskScore))}>
                      Risk: {flow.riskScore}/10
                    </span>
                    {flow.encrypted && <Lock className="w-3 h-3 text-green-400" />}
                    {flow.crossBorder && <Globe className="w-3 h-3 text-yellow-400" />}
                  </div>
                </div>
                <div className="flex items-center justify-between text-gray-400">
                  <span>{flow.dataType} • {flow.classification}</span>
                  <span>{flow.volume}/hr</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold text-white mb-4">Compliance Actions</h3>
        <div className="flex flex-wrap gap-3">
          <Button variant="primary" size="sm" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Generate Compliance Report
          </Button>
          <Button variant="secondary" size="sm" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Run Security Audit
          </Button>
          <Button variant="secondary" size="sm" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Export Data Flows
          </Button>
          <Button variant="secondary" size="sm" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Risk Assessment
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ComplianceMonitor;